import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ReportMultiSelect } from '../../components/reports/ReportMultiSelect';
import { ReportExportToolbar } from '../../components/reports/ReportExportToolbar';
import { ReportTaskStatusBadge, ReportUrgencyBadge } from '../../components/reports/ReportBadges';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { defaultDashboardDateRange, todayIsoDate, validateReportDateRange } from '../../utils/reportDates';
import {
  downloadReportBlob,
  exportCaseTrackingReport,
  searchCaseTrackingReport,
  type CaseTrackingReportFilters,
} from '../../services/caseTrackingReports';
import type { CaseTrackingRow } from '../../data/reports';
import { ReportFilterActions } from './ReportFilterActions';
import { useCaseTrackingLookups } from './useCaseTrackingLookups';

type Filters = {
  fileCategories: string[];
  subFileCategories: string[];
  submittedAgencies: string[];
  taskStatuses: string[];
  urgencyLevels: string[];
  workflowStages: string[];
  allocationOfficers: string[];
  assignedOfficers: string[];
  supervisors: string[];
  from: string;
  to: string;
};

function createEmptyFilters(): Filters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    fileCategories: [],
    subFileCategories: [],
    submittedAgencies: [],
    taskStatuses: [],
    urgencyLevels: [],
    workflowStages: [],
    allocationOfficers: [],
    assignedOfficers: [],
    supervisors: [],
    from,
    to,
  };
}

function createEmptyFilterIds(): CaseTrackingReportFilters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    fileCategoryIds: [],
    subFileCategoryIds: [],
    submittedAgencyIds: [],
    taskStatusIds: [],
    urgencyLevelIds: [],
    workflowStageIds: [],
    allocationOfficerIds: [],
    assignedOfficerIds: [],
    supervisorIds: [],
    dateFrom: from,
    dateTo: to,
  };
}

function exportFilename(format: 'pdf' | 'excel'): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return format === 'pdf' ? `case-tracking-report-${stamp}.pdf` : `case-tracking-report-${stamp}.xlsx`;
}

export default function CaseTrackingReportPage() {
  const { t } = useTranslation();
  const maxDate = todayIsoDate();
  const [draft, setDraft] = useState<Filters>(createEmptyFilters);
  const [appliedFilterIds, setAppliedFilterIds] = useState<CaseTrackingReportFilters>(createEmptyFilterIds);
  const [rows, setRows] = useState<CaseTrackingRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [resolvedSearchKey, setResolvedSearchKey] = useState<string | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const searchRequestKey = useMemo(
    () => `${searchTrigger}:${page}:${pageSize}:${JSON.stringify(appliedFilterIds)}`,
    [appliedFilterIds, page, pageSize, searchTrigger],
  );
  const loading = resolvedSearchKey !== searchRequestKey;

  const {
    agencyNames,
    taskStatusNames,
    workflowStageNames,
    allocationOfficerNames,
    assignedOfficerNames,
    supervisorNames,
    fileCategoryNames,
    subFileCategoryNames,
    urgencyLevelNames,
    mapAgencyNamesToIds,
    mapTaskStatusNamesToIds,
    mapWorkflowStageNamesToIds,
    mapAllocationOfficerNamesToIds,
    mapAssignedOfficerNamesToIds,
    mapSupervisorNamesToIds,
    mapFileCategoryNamesToIds,
    mapSubFileCategoryNamesToIds,
    mapUrgencyLevelNamesToIds,
    subFileCategoryDisabled,
    workflowStageDisabled,
    lookupsLoading,
    subLookupsLoading,
    workflowStagesLoading,
  } = useCaseTrackingLookups(draft.fileCategories, draft.subFileCategories);

  const buildFilterIds = useCallback(
    (source: Filters): CaseTrackingReportFilters => ({
      fileCategoryIds: mapFileCategoryNamesToIds(source.fileCategories),
      subFileCategoryIds: mapSubFileCategoryNamesToIds(source.subFileCategories),
      submittedAgencyIds: mapAgencyNamesToIds(source.submittedAgencies),
      taskStatusIds: mapTaskStatusNamesToIds(source.taskStatuses),
      urgencyLevelIds: mapUrgencyLevelNamesToIds(source.urgencyLevels),
      workflowStageIds: mapWorkflowStageNamesToIds(source.workflowStages),
      allocationOfficerIds: mapAllocationOfficerNamesToIds(source.allocationOfficers),
      assignedOfficerIds: mapAssignedOfficerNamesToIds(source.assignedOfficers),
      supervisorIds: mapSupervisorNamesToIds(source.supervisors),
      dateFrom: source.from,
      dateTo: source.to,
    }),
    [
      mapAgencyNamesToIds,
      mapAllocationOfficerNamesToIds,
      mapAssignedOfficerNamesToIds,
      mapFileCategoryNamesToIds,
      mapSubFileCategoryNamesToIds,
      mapSupervisorNamesToIds,
      mapTaskStatusNamesToIds,
      mapUrgencyLevelNamesToIds,
      mapWorkflowStageNamesToIds,
    ],
  );

  const validateDates = useCallback(
    (from: string, to: string) => {
      const err = validateReportDateRange(from, to);
      if (err) {
        setFlashToast({ message: t(`reports.dateValidation.${err}`), variant: 'error' });
        return false;
      }
      return true;
    },
    [t],
  );

  const runExport = useCallback(
    async (format: 'pdf' | 'excel', source: Filters) => {
      if (!validateDates(source.from, source.to)) return;
      const apiFormat = format === 'pdf' ? 'PDF' : 'EXCEL';
      const res = await exportCaseTrackingReport(apiFormat, buildFilterIds(source));
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.exportFailed'), variant: 'error' });
        throw new Error(res.message ?? 'export failed');
      }
      downloadReportBlob(res.data.blob, res.data.fileName ?? exportFilename(format));
      setFlashToast({ message: t('reports.generateSuccess'), variant: 'success' });
    },
    [buildFilterIds, t, validateDates],
  );

  useEffect(() => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    void searchCaseTrackingReport(appliedFilterIds, page - 1, pageSize).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.searchFailed'), variant: 'error' });
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        setResolvedSearchKey(searchRequestKey);
        return;
      }

      setRows(res.data.content);
      setTotalCount(res.data.totalElements);
      setTotalPages(Math.max(1, res.data.totalPages));
      setResolvedSearchKey(searchRequestKey);
    });

    return () => controller.abort();
  }, [appliedFilterIds, page, pageSize, searchRequestKey, t]);

  const columns = useMemo<Column<CaseTrackingRow>[]>(
    () => [
      {
        key: 'fileRef',
        header: t('reports.ctr.colFileRef'),
        getValue: (r) => r.fileRef,
        render: (r) => <span className="rpt-td-ref">{r.fileRef}</span>,
      },
      { key: 'fileCategory', header: t('reports.ctr.colFileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('reports.ctr.colSubFileCategory'), getValue: (r) => r.subFileCategory },
      { key: 'submittedAgency', header: t('reports.ctr.colSubmittedAgency'), getValue: (r) => r.submittedAgency },
      {
        key: 'submittedAt',
        header: t('reports.ctr.colSubmittedAt'),
        getValue: (r) => r.submittedAt,
        render: (r) => formatDocumentTableDateTime(r.submittedAt),
      },
      { key: 'workflowStage', header: t('reports.ctr.colWorkflowStage'), getValue: (r) => r.workflowStage },
      { key: 'allocationOfficer', header: t('reports.ctr.colAllocationOfficer'), getValue: (r) => r.allocationOfficer },
      { key: 'assignedOfficer', header: t('reports.ctr.colAssignedOfficer'), getValue: (r) => r.assignedOfficer },
      { key: 'supervisor', header: t('reports.ctr.colSupervisor'), getValue: (r) => r.supervisor },
      {
        key: 'taskStatus',
        header: t('reports.ctr.colTaskStatus'),
        getValue: (r) => r.taskStatus,
        render: (r) => <ReportTaskStatusBadge status={r.taskStatus} />,
      },
      {
        key: 'urgencyLevel',
        header: t('reports.ctr.colUrgencyLevel'),
        getValue: (r) => r.urgencyLevel,
        render: (r) => <ReportUrgencyBadge level={r.urgencyLevel} />,
      },
    ],
    [t],
  );

  return (
    <>
      <div className="bc">
        <Link to="/reports/case-tracking">{t('reports.breadcrumb')}</Link>
        <span className="bc-sep">/</span>
        <span>{t('reports.ctr.title')}</span>
      </div>

      <div className="pg-title" style={{ marginBottom: 20 }}>
        {t('reports.ctr.title')}
      </div>

      <div className="flt-card rpt-filter-card">
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.fileCategories')}
            value={draft.fileCategories}
            options={fileCategoryNames}
            placeholder={t('reports.filters.selectFileCategories')}
            disabled={lookupsLoading}
            onChange={(fileCategories) =>
              setDraft((d) => ({
                ...d,
                fileCategories,
                subFileCategories: [],
                workflowStages: [],
              }))
            }
          />
          <ReportMultiSelect
            label={t('reports.filters.subFileCategories')}
            value={draft.subFileCategories}
            options={subFileCategoryNames}
            placeholder={t('reports.filters.selectSubFileCategories')}
            disabled={subFileCategoryDisabled || lookupsLoading || subLookupsLoading}
            onChange={(subFileCategories) =>
              setDraft((d) => ({ ...d, subFileCategories, workflowStages: [] }))
            }
          />
          <ReportMultiSelect
            label={t('reports.filters.submittedAgency')}
            value={draft.submittedAgencies}
            options={agencyNames}
            placeholder={t('reports.filters.selectAgency')}
            disabled={lookupsLoading}
            onChange={(submittedAgencies) => setDraft((d) => ({ ...d, submittedAgencies }))}
          />
        </div>
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.taskStatus')}
            value={draft.taskStatuses}
            options={taskStatusNames}
            placeholder={t('reports.filters.selectStatus')}
            disabled={lookupsLoading}
            onChange={(taskStatuses) => setDraft((d) => ({ ...d, taskStatuses }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.urgencyLevel')}
            value={draft.urgencyLevels}
            options={urgencyLevelNames}
            placeholder={t('reports.filters.selectUrgencyLevel')}
            disabled={lookupsLoading}
            onChange={(urgencyLevels) => setDraft((d) => ({ ...d, urgencyLevels }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.workflowStage')}
            value={draft.workflowStages}
            options={workflowStageNames}
            placeholder={t('reports.filters.selectStage')}
            disabled={workflowStageDisabled || lookupsLoading || workflowStagesLoading}
            onChange={(workflowStages) => setDraft((d) => ({ ...d, workflowStages }))}
          />
        </div>
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.allocationOfficer')}
            value={draft.allocationOfficers}
            options={allocationOfficerNames}
            placeholder={t('reports.filters.selectAllocationOfficer')}
            disabled={lookupsLoading}
            onChange={(allocationOfficers) => setDraft((d) => ({ ...d, allocationOfficers }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.assignedOfficer')}
            value={draft.assignedOfficers}
            options={assignedOfficerNames}
            placeholder={t('reports.filters.selectAssignedOfficer')}
            disabled={lookupsLoading}
            onChange={(assignedOfficers) => setDraft((d) => ({ ...d, assignedOfficers }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.supervisor')}
            value={draft.supervisors}
            options={supervisorNames}
            placeholder={t('reports.filters.selectSupervisor')}
            disabled={lookupsLoading}
            onChange={(supervisors) => setDraft((d) => ({ ...d, supervisors }))}
          />
        </div>
        <div className="rpt-filter-grid-actions">
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.from')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.from}
              maxDate={draft.to || maxDate}
              onChange={(from) => setDraft((d) => ({ ...d, from }))}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.to')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.to}
              minDate={draft.from || undefined}
              maxDate={maxDate}
              onChange={(to) => setDraft((d) => ({ ...d, to }))}
            />
          </div>
          <ReportFilterActions
            onReset={() => {
              setDraft(createEmptyFilters());
              setAppliedFilterIds(createEmptyFilterIds());
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
            onSearch={() => {
              if (!validateDates(draft.from, draft.to)) return;
              setAppliedFilterIds(buildFilterIds(draft));
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
          />
        </div>
      </div>

      <DataTable
        title={t('reports.previewTitle')}
        toolbarRight={
          <ReportExportToolbar
            disabled={lookupsLoading}
            onGenerate={(format) => runExport(format, draft)}
          />
        }
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        enableHeaderSearch={false}
        enableSorting={false}
        emphasizedHeader
        rowsPerPageOptions={[10, 30, 50]}
        defaultRowsPerPage={10}
        footerCountNoun={t('reports.records')}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (nextPage) => {
            setPage(nextPage);
          },
          onPageSizeChange: (nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          },
        }}
      />
    </>
  );
}
